"use client";

import React from "react";
import { MapPin, Phone, Mail, Loader, MessageSquare } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
const contactDetails = {
  address: "Via Lorenzo Ghiberti 21, 35134 Padova",
  phone: "+39 049 86 40 662",
  email: "info@worldimmigration.it"
};
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-card p-8 border border-border rounded-lg">
        <h3 className="font-default text-2xl font-bold text-foreground mb-4">
          Messaggio Inviato!
        </h3>
        <p className="font-default text-muted-foreground mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} className="rounded-lg font-default uppercase px-8 py-4 h-auto bg-[#FFC829] text-primary hover:bg-[#FFC829]/90">
          Invia un Nuovo Messaggio
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Il nome è obbligatorio"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-white uppercase text-xs tracking-widest font-semibold">Nome Completo</FormLabel>
            <FormControl>
              <Input placeholder="Inserisci il tuo nome" className="rounded-md border-border focus-visible:ring-secondary focus-visible:border-secondary bg-primary text-white" {...field} />
            </FormControl>
            <FormDescription className="font-default text-xs opacity-70">
              Inserisca il suo nome per una risposta personalizzata.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "L'e-mail è obbligatoria",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "E-mail non valida"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-white uppercase text-xs tracking-widest font-semibold">E-mail</FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@esempio.it" className="rounded-md border-border focus-visible:ring-secondary focus-visible:border-secondary bg-primary text-white" {...field} />
              </FormControl>
              <FormDescription className="font-default text-xs opacity-70">
                Utilizzeremo questo indirizzo per ricontattarla.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-white uppercase text-xs tracking-widest font-semibold">Telefono</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+39 000 0000000" className="rounded-md border-border focus-visible:ring-secondary focus-visible:border-secondary bg-primary text-white" {...field} />
              </FormControl>
              <FormDescription className="font-default text-xs opacity-70">
                Opzionale – per un contatto telefonico diretto.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-white uppercase text-xs tracking-widest font-semibold">Oggetto</FormLabel>
            <FormControl>
              <Input placeholder="Es.: Richiesta Visto Business" className="rounded-md border-border focus-visible:ring-secondary focus-visible:border-secondary bg-primary text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Il messaggio è obbligatorio",
      minLength: {
        value: 10,
        message: "Il messaggio deve contenere almeno 10 caratteri"
      },
      maxLength: {
        value: 1000,
        message: "Il messaggio può contenere al massimo 1000 caratteri"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-white uppercase text-xs tracking-widest font-semibold">Messaggio</FormLabel>
            <FormControl>
              <Textarea placeholder="Descriva brevemente la sua richiesta..." className="rounded-md border-border focus-visible:ring-secondary focus-visible:border-secondary bg-primary text-white min-h-[150px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-[#FFC829] hover:bg-[#FFC829]/90 text-primary rounded-lg font-default font-bold uppercase px-10 py-6 h-auto text-base tracking-widest transition-all duration-300 shadow-lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Invio in corso...
          </> : "Invia Richiesta"}
      </Button>
    </Form>;
}
export default function EnvieSuaMensagem() {
  return <section data-section-id="224961" id="richiedi-informazioni" className="bg-background text-foreground py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div className="flex flex-col justify-center">
            <h2 className="font-default text-4xl md:text-5xl font-bold text-primary mb-4 tracking-tight">
              Contatti Istituzionali
            </h2>
            <p className="font-default text-secondary font-semibold mb-12 uppercase tracking-wider text-sm">
              Eccellenza nella mobilità internazionale e servizi corporate premium
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center shrink-0 border border-secondary/30 shadow-md">
                  <MapPin className="size-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-semibold text-primary mb-1">
                    Sede Legale
                  </h3>
                  <p className="font-default text-muted-foreground leading-relaxed">
                    {contactDetails.address}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center shrink-0 border border-secondary/30 shadow-md">
                  <Phone className="size-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-semibold text-primary mb-1">
                    Centralino
                  </h3>
                  <Link to={`tel:${contactDetails.phone.replace(/\D/g, "")}`} className="font-default text-muted-foreground hover:text-secondary transition-colors">
                    {contactDetails.phone}
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center shrink-0 border border-secondary/30 shadow-md">
                  <MessageSquare className="size-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-semibold text-primary mb-1">
                    WhatsApp
                  </h3>
                  <Link to="https://wa.me/393458474911" newTab className="font-default text-muted-foreground hover:text-secondary transition-colors">
                    +39 345 84 74 911
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center shrink-0 border border-secondary/30 shadow-md">
                  <Mail className="size-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-semibold text-primary mb-1">
                    Corrispondenza
                  </h3>
                  <Link to={`mailto:${contactDetails.email}`} className="font-default text-muted-foreground hover:text-secondary transition-colors">
                    {contactDetails.email}
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center shrink-0 border border-secondary/30 shadow-md">
                  <Mail className="size-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-semibold text-primary mb-1">
                    Company Services
                  </h3>
                  <Link to="mailto:aziende@worldimmigration.it" className="font-default text-muted-foreground hover:text-secondary transition-colors">
                    aziende@worldimmigration.it
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center shrink-0 border border-secondary/30 shadow-md">
                  <Mail className="size-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-semibold text-primary mb-1">
                    General Info
                  </h3>
                  <Link to="mailto:info@worldimmigration.it" className="font-default text-muted-foreground hover:text-secondary transition-colors">
                    info@worldimmigration.it
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-primary p-8 md:p-10 shadow-2xl border border-white/10 rounded-lg">
            <h3 className="font-default text-2xl font-bold text-white mb-8 border-l-4 border-secondary pl-4">
              Richiedi una Consulenza
            </h3>
            <FormProvider formKey="information_request_form_students_and_institutes" formId="information_request_form_students_and_institutes" sectionName="richiedi-informazioni" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 1000
            }
          }} submitText="Invia Richiesta" successMessage="La ringraziamo per averci contattato. Un nostro esperto prenderà in carico la sua richiesta entro 24 ore lavorative.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}